import React, { useRef, useState, memo, useMemo } from 'react';
import { MenuItem, DropdownButton, Button } from 'react-bootstrap';
import Select, { components } from 'react-select';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import { Store } from 'react-notifications-component';
import classNames from 'classnames';
import _ from 'lodash';
import PropTypes from 'prop-types';

import ServicesDropdown from '../../../../../../../components/Dropdown/ServicesDropdown';
import Tooltip from '../../../../../../../components/Tooltip';
import TableMassActions from '../../../../../../../components/Table/MassActions';

import { delay } from '../../../../../../../utils/helper';
import {
  statusesList,
  serviceTypeList,
  providersList,
  getTypeBtnName,
  getCurrentServicesId,
  subcategoriesOptions, getSubcategoryBtnName,
} from './helper';
import {
  changeFilterStatus,
  changeFilterType,
  changeFilterProvider,
  changeToggleCategories,
  openModal,
  serviceTableLoading,
  updateRateError,
  updateRateSuccess, changeFilterSubcategory,
} from '../../../../reducer';
import { getErrorMessage, getStatusName } from '../../../../helpers/getters';
import { getCategoriesList, servicesCheckedMassAdd, servicesCheckedMassRemove } from '../../../../../../../redux/actions/services';
import { MODALS } from '../../../../constants';
import { serviceServicesConvertRate, serviceServicesConvertRateCheck } from '../../../../../../../services/endpoints/services';

export const DropdownIndicator = () => {
  const { darkMode } = useSelector((state) => state.general);

  return (
    <div
      style={{
        color: darkMode ? 'rgba(103, 107, 118, 0.5)' : '#a0a0a0',
        height: 24,
        width: 32,
        justifyContent: 'center',
        display: 'flex',
        alignItems: 'center',
      }}
    >
      <span className="fa fa-search" />
    </div>
  );
};

export const ClearIndicator = (props) => (
  <components.ClearIndicator {...props} className="service-table__provider-dropdown-clear">
    <span className="fa fa-close" />
  </components.ClearIndicator>
);

const ServicesHeader = ({ serviceTable, list, massActions, config, servicesChecked, serviceDispatch }) => {
  const { startList, search, filters, toggleCategories } = serviceTable;

  const history = useHistory();
  const dispatch = useDispatch();

  const { darkMode } = useSelector((state) => state.general);

  const showProviderColumn = config && config.access && config.access.see_provider_column;
  const disableMassActions = !list.length && !servicesChecked.length;

  const onChangeShowHide = () => {
    serviceDispatch(changeToggleCategories(!toggleCategories));
  };

  const updateConvertRate = async (list) => {
    try {
      const response = await serviceServicesConvertRate(list);
      if (response?.data) {
        const taskId = response?.data?.id;
        await delay(500);
        return taskId;
      }
    } catch (error) {
      // const errorMessage = getErrorMessage(error);
      await delay(500);
      return Promise.reject(error);
    }
  };

  /**
   *
   * @param taskId
   * @description
   * Статусы:
   * - waiting
   * - success
   * - fail
   * @returns {Promise<JSX.Element|*>}
   */
  const updateConvertCheck = async (taskId) => {
    try {
      const delayCount = config?.update_rate_request_interval ? config.update_rate_request_interval * 1000 : 1000;
      const response = await serviceServicesConvertRateCheck(taskId);
      if (response?.data) {
        const status = response?.data?.status;
        switch (status) {
        case 'waiting':
          await delay(delayCount);
          return updateConvertCheck(taskId);
        case 'success':
          return true;
        case 'fail':
          // eslint-disable-next-line prefer-promise-reject-errors
          return Promise.reject('Service check fail');
        default:
          await delay(delayCount);
          return updateConvertCheck(taskId);
        }
      }
    } catch (error) {
      const errorMessage = getErrorMessage(error);
      await delay(500);
      return Promise.reject(errorMessage);
    }
  };

  const onUpdateConvertRate = async () => {
    const sendError = (errorMessage, type = 'danger') => Store.addNotification({
      title: type === 'danger' ? 'Error' : 'Warning',
      message: errorMessage,
      type,
      insert: 'top',
      container: 'bottom-right',
      animationIn: ['animate__animated', 'animate__fadeIn'],
      animationOut: ['animate__animated', 'animate__fadeOut'],
      dismiss: {
        duration: 3000,
      },
    });

    try {
      await serviceDispatch(serviceTableLoading());
      updateConvertRate(servicesChecked)
        .then((taskId) => updateConvertCheck(taskId).then(() => dispatch(getCategoriesList())))
        .then(() => serviceDispatch(updateRateSuccess(servicesChecked)))
        .then(() => dispatch(servicesCheckedMassRemove()))
        .catch((error) => {
          const errorMessage = getErrorMessage(error);
          const type = errorMessage === 'Nothing to update' ? 'warning' : 'danger';
          sendError(errorMessage, type);
          dispatch(servicesCheckedMassRemove());
          serviceDispatch(updateRateError());
          return false;
        });
      return true;
    } catch (error) {
      const errorMessage = getErrorMessage(error);
      sendError(errorMessage);
      await dispatch(servicesCheckedMassRemove());
      await serviceDispatch(updateRateError());
      return false;
    }
  };

  const onChangeAction = (selectedValue) => {
    const { value } = selectedValue;

    switch (value) {
    case MODALS.EDIT_RATES:
      history.push('/services/mass-edit');
      return;
    case MODALS.UPDATE_CURRENCY_CONVERSION_RATE:
      return onUpdateConvertRate();
    case MODALS.CHANGE_DRIP_FEED_ALL:
      return serviceDispatch(openModal(value, selectedValue));
    default:
      return serviceDispatch(openModal(value, null));
    }
  };

  const isCheckedMass = !!servicesChecked.length;
  const onChangeMassCheckbox = () => {
    if (isCheckedMass) {
      dispatch(servicesCheckedMassRemove());
    } else {
      const listCheckedId = getCurrentServicesId(startList, filters, search);
      dispatch(servicesCheckedMassAdd(listCheckedId));
    }
  };

  const dropdownNode = useRef(null);
  const [isOpenProviders, setOpenProviders] = useState(false);
  const outSideClickProviders = (e) => {
    if (dropdownNode.current && dropdownNode.current.contains(e.target)) {
      return false;
    }
    document.removeEventListener('mousedown', outSideClickProviders, false);
    return setOpenProviders(false);
  };

  const onOpenProviders = () => {
    if (isOpenProviders) {
      document.removeEventListener('mousedown', outSideClickProviders, false);
      setOpenProviders(false);
    } else {
      document.addEventListener('mousedown', outSideClickProviders, false);
      setOpenProviders(true);
    }
  };

  const filterProviderValue = filters.provider.value;
  const providers = useMemo(() => providersList(startList), [startList]);
  const onChangeProvider = (value) => {
    serviceDispatch(changeFilterProvider(value.length ? value : null));
  };

  const statusFilterStatusValue = filters.status.value;
  const statusFilterIsActive = _.isNumber(statusFilterStatusValue);
  const statusBtnName = useMemo(() => getStatusName(filters.status.value), [filters.status.value]);
  const statuses = useMemo(() => statusesList(startList), [startList]);
  const onChangeStatus = (status) => {
    if (statusFilterStatusValue !== status) {
      serviceDispatch(changeFilterStatus(status));
    }
  };

  const types = serviceTypeList(startList);
  const typeFilterValue = filters.type.value;
  const typeFilterActive = _.isNumber(typeFilterValue);
  const typeBtnName = useMemo(() => getTypeBtnName(typeFilterValue), [typeFilterValue]);
  const onChangeType = (type) => {
    if (typeFilterValue !== type) {
      serviceDispatch(changeFilterType(type));
    }
  };

  const subcategoryFilterValue = filters.subcategory.value;
  const subcategoryFilterActive = !!filters.subcategory.value;
  const subcategoryFilters = useMemo(() => subcategoriesOptions(startList), [startList]);
  const subcategoryBtnNane = useMemo(() => getSubcategoryBtnName(subcategoryFilterValue), [subcategoryFilterValue])
  const onChangeSubcategory = (subcategory) => {
    if (subcategoryFilterValue !== subcategory) {
      serviceDispatch(changeFilterSubcategory(subcategory));
    }
  };

  return (
    <div className="service-table__header-wrapper">
      <table className="service-table__header">
        <thead>
          <tr>
            <th className="service-table__checker text-center">
              {massActions && massActions.length ? (
                <TableMassActions
                  count={servicesChecked.length}
                  counterTitle="services"
                  checked={isCheckedMass}
                  disabled={disableMassActions}
                  onChange={onChangeMassCheckbox}
                >
                  <ServicesDropdown id="mass-actions" handleSelect={onChangeAction} bsSize="xsmall" title="Actions" items={massActions} />
                </TableMassActions>
              ) : null}
            </th>
            <th className="service-table__id">ID</th>
            <th className="service-table__service dropdown-m-height">
              {subcategoryFilters.length && config.access?.see_subcategories ? (
                <DropdownButton
                  bsStyle="default"
                  title={subcategoryBtnNane}
                  className={classNames('service-table__filter-btn', {
                    active: subcategoryFilterActive,
                  })}
                  id="dropdown-subcategories"
                >
                  {subcategoryFilters.map((subcategory) => (
                    <MenuItem key={subcategory.value} eventKey={subcategory.value} active={subcategory.value === subcategoryFilterValue} onClick={() => onChangeSubcategory(subcategory.value)}>
                      {subcategory.label}
                      {' '}
                      (
                      {subcategory.count}
                      )
                    </MenuItem>
                  ))}
                </DropdownButton>
              ) : (
                'Service'
              )}
            </th>
            <th className="service-table__type">
              {types.length ? (
                <DropdownButton
                  bsStyle="default"
                  title={typeBtnName}
                  className={classNames('service-table__filter-btn', {
                    active: typeFilterActive,
                  })}
                  id="dropdown-filter-type"
                >
                  {types.map((type) => (
                    <MenuItem key={type.id} eventKey={type.id} active={type.id === typeFilterValue} onClick={() => onChangeType(type.id)}>
                      {type.name}
                      {' '}
                      (
                      {type.count}
                      )
                    </MenuItem>
                  ))}
                </DropdownButton>
              ) : (
                'Type'
              )}
            </th>
            {showProviderColumn ? (
              <th className="service-table__provider">
                {providers.length ? (
                  <div className="service-table__provider-dropdown" ref={dropdownNode}>
                    <Button
                      className="service-table__filter-btn"
                      onClick={onOpenProviders}
                      active={!!filterProviderValue}
                      id="dropdown-filter-provider"
                    >
                      Provider
                      {' '}
                      {filterProviderValue ? `(${filterProviderValue.length})` : null}
                      {' '}
                      <span className="caret" />
                    </Button>
                    {isOpenProviders ? (
                      <div className="service-table__provider-dropdown-menu">
                        <Select
                          autoFocus
                          menuIsOpen
                          isMulti
                          components={{ DropdownIndicator, ClearIndicator, IndicatorSeparator: null }}
                          onChange={onChangeProvider}
                          noOptionsMessage={() => 'No providers'}
                          controlShouldRenderValue={false}
                          hideSelectedOptions={false}
                          value={filterProviderValue}
                          options={providers}
                          styles={{
                            container: (styles) => ({
                              ...styles,
                              width: '270px',
                              border: '1px solid rgba(0,0,0,.15)',
                              background: darkMode ? '#222327' : '#ffffff',
                              borderRadius: '4px',
                              paddingTop: '7px',
                              paddingLeft: '7px',
                              paddingRight: '7px',
                              boxShadow: '0 6px 12px rgba(0,0,0,.175)',
                            }),
                            valueContainer: (styles) => ({
                              ...styles,
                              paddingTop: '2px',
                              paddingBottom: '2px',
                              paddingLeft: '0px',
                              paddingRight: '8px',
                            }),
                            menu: (styles) => ({
                              ...styles,
                              marginTop: '0px',
                              boxShadow: 'none',
                              border: 'none',
                              borderTopLeftRadius: '0px',
                              borderTopRightRadius: '0px',
                              position: 'relative',
                              background: darkMode ? '#222327' : '#ffffff',
                            }),
                            input: (styles) => ({
                              ...styles,
                              color: darkMode ? '#dfe0e1' : '#333',
                            }),
                            control: (styles) => ({
                              ...styles,
                              minHeight: '34px',
                              flexDirection: 'row-reverse',
                              borderBottomWidth: '1px',
                              border: darkMode ? 'inset 0 2px 4px 0 rgb(0 0 0 / 20%)' : '1px solid #ddd',
                              boxShadow: 'inset 0 1px 1px rgba(0,0,0,.075)',
                              cursor: 'text',
                              color: darkMode ? '#dfe0e1' : '#333',
                              background: darkMode ? '#2e3036' : '#ffffff',
                              borderColor: 'rgba(103, 107, 118, 0.5)',
                              '&:hover': {
                                border: darkMode ? 'solid 1px rgba(103, 107, 118, 0.5)' : '1px solid #ccc',
                              },
                            }),
                            // eslint-disable-next-line no-unused-vars
                            option: (styles, { data, isSelected }) => ({
                              ...styles,
                              paddingLeft: '10px',
                              paddingRight: '10px',
                              paddingTop: '3px',
                              paddingBottom: '3px',
                              cursor: 'pointer',
                              color: darkMode ? '#dfe0e1' : '#333',
                              background: darkMode ? '#222327' : '#ffffff',
                              '&:hover': {
                                background: darkMode ? '#2e3036' : '#f5f5f5',
                              },
                              '&:before': isSelected
                                ? {
                                  content: '"\\e013"',
                                  fontFamily: 'Glyphicons Halflings',
                                  fontStyle: 'normal',
                                  fontWeight: 400,
                                  position: 'absolute',
                                  right: '4px',
                                }
                                : null,
                            }),
                          }}
                          getOptionLabel={(option) => `${option.label} (${option.count})`}
                          placeholder="Search"
                        />
                      </div>
                    ) : null}
                  </div>
                ) : (
                  'Provider'
                )}
              </th>
            ) : null}
            <th className="service-table__rate">Rate</th>
            <th className="service-table__min">Min</th>
            <th className="service-table__max">Max</th>
            <th className="service-table__visibility">
              {statuses.length ? (
                <DropdownButton
                  bsStyle="default"
                  title={statusBtnName}
                  className={classNames('service-table__filter-btn', {
                    active: statusFilterIsActive,
                  })}
                  id="dropdown-filter-status"
                >
                  {statuses.map((status) => (
                    <MenuItem
                      key={status.value}
                      eventKey={status.value}
                      active={status.value === statusFilterStatusValue}
                      onClick={() => onChangeStatus(status.value)}
                    >
                      {status.name}
                      {' '}
                      (
                      {status.count}
                      )
                    </MenuItem>
                  ))}
                </DropdownButton>
              ) : (
                'Status'
              )}
            </th>
            <th className="service-table__action text-right">
              <Tooltip tooltipClass="tooltip__left" placement="left" text={toggleCategories ? 'Hide all' : 'Show all'}>
                <span
                  onClick={onChangeShowHide}
                  className={classNames('far service-table__header-hide-all', {
                    'fa-compress-alt': toggleCategories,
                    'fa-expand-alt': !toggleCategories,
                  })}
                />
              </Tooltip>
            </th>
          </tr>
        </thead>
      </table>
    </div>
  );
};

ServicesHeader.propTypes = {
  serviceTable: PropTypes.object,
  list: PropTypes.array,
  massActions: PropTypes.array,
  config: PropTypes.object,
  servicesChecked: PropTypes.array,
  serviceDispatch: PropTypes.func,
};

const arePropsEqual = (prevProps, nextProps) => {
  const prev = [prevProps.serviceTable.filters, prevProps.serviceTable.startList, prevProps.servicesChecked, prevProps.serviceTable.list];
  const next = [nextProps.serviceTable.filters, nextProps.serviceTable.startList, nextProps.servicesChecked, nextProps.serviceTable.list];
  return _.isEqual(prev, next);
};

export default memo(ServicesHeader, arePropsEqual);
